import { $api } from 'src/plugins'
import { uniq } from 'lodash'

class AppStore {
  /**
  * 将初始化的步骤移动到 constructor 中，
  * 并且请务必初始化，否则 babel 插件 plugin-transform-flow-strip-types 编译失败，
  * 相关联 issue: 
  * https://github.com/react-navigation/react-navigation/issues/5825#issuecomment-590723220
  * https://github.com/facebook/react-native/issues/20150#issuecomment-417858270
  */
  /**
   * 更新对象类型的 store 时候，
   * 除非要观察整个对象，否则禁止完全重写整个对象，
   * 因为完全重写会导致观察该对象的所有组件都重新渲染
   */
  // 微前端主应用传递给微应用的数据
  props: any
  /**
   * current 只是子模块（具体应用）内某个 store 的引用
   * 比如：src/app/rtm/stores/rtm.ts rtmStore
   * 那么 current 就指代 rtmStore
   */
  current: any
  enums: any[]

  constructor () {
    this.props = {}
    this.current = {}
    this.enums = []
  }

  /**
   * mount 微应用的 props
   * @param props 
   */
  mount (props: any = {}) {
    this.props = props
  }

  /**
   * 注册子应用的 store，以便在全局通过 appStore.current 引用到子应用的 store
   * @param store 
   */
  register (store: any) {
    this.current = store
  }

  /**
   * 生成应用间跳转签名
   * @returns 
   */
  generateSignature (): Promise<string> {
    return $api['app/generateSignature']()
  }

  /**
   * 获取枚举
   * @param names 枚举名称
   */
  getEnums (names: string[]): Promise<any[]> {
    // 去重
    names = uniq(names).filter(item => !!item)
    // 过滤出已经加载过的枚举名称
    const loadedEnums = this.enums.filter(item => names.indexOf(item.EnumName) !== -1)
    // 过滤出没加载过的枚举名称
    const filter = names.filter(name => !loadedEnums.find(item => item.EnumName === name))
    if (filter.length) {
      return $api['app/getEnums'](
        { EnumNames: filter.join(',') },
        { throttle: true, noErrorToast: true }
      ).then((data: any[]) => {
        if (data) {
          this.enums = data.concat(this.enums)
          // 存在 getEnums 返回所有枚举的情况，所以这边保证返回的是想要的枚举，而不是所有枚举，因为所有枚举数据量太大
          return data.filter(item => filter.indexOf(item.EnumName) !== -1).concat(loadedEnums)
        }
        return loadedEnums
      }).catch(() => {
        /**
         * 同一个页面多个组件请求枚举时，会被 throttle 掉
         * 因此起一个定时器，让被 throttle 掉的请求也能获取到枚举
         */
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(loadedEnums.length ? loadedEnums : this.enums)
          }, 1000)
        })
      })
    } else {
      return Promise.resolve(loadedEnums)
    }
  }

  /**
   * 获取枚举的 Options 用于 Select.Options
   * @param name 枚举名称
   * @returns 
   */
  getEnumOptions (name: string, enums?: any[]) {
    enums = enums || this.enums
    return enums.find(item => item.EnumName === name)?.options || []
  }

  /**
   * 获取枚举值对应的 Label
   * @param name 枚举名称
   * @param value 枚举值
   * @returns 
   */
  getEnumLabel (name: string, value?: string | number) {
    return this.getEnumOptions(name)?.find((item: any) => item.value === value)?.label
  }
}

const appStore = new AppStore()

export default appStore